import React from "react";
import { Button, Card, Icon, Text, VStack } from "@astryxdesign/core";
import type { IconType } from "@astryxdesign/core";
import styles from "./GreetingCard.module.css";

type TimeOfDay = "morning" | "afternoon" | "evening" | "night";

export interface GreetingCardProps {
  /** One line under the greeting, saying what the viewer can do next. */
  description: string;
  /** Label for the card's primary action. */
  actionLabel: string;
  /** Where the primary action navigates. */
  actionHref: string;
  /** Icon rendered before the action's label. */
  actionIcon?: IconType;
}

/** Morning 5–12, afternoon 12–17, evening 17–21, night 21–5 (local time). */
const timeOfDayAt = (hour: number): TimeOfDay => {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
};

// "Good night" reads as a goodbye, so night keeps the evening greeting; the
// moon and stars carry the difference.
const GREETINGS: Record<TimeOfDay, string> = {
  morning: "Good morning",
  afternoon: "Good afternoon",
  evening: "Good evening",
  night: "Good evening",
};

const PALETTE_CLASS: Record<TimeOfDay, string> = {
  morning: styles.morning,
  afternoon: styles.afternoon,
  evening: styles.evening,
  night: styles.night,
};

// Positions in the 1200×300 scene. The hills stay low on the left so the
// greeting always sits on open sky; the sun and stars stay right of the text.
const SUN_POSITION: Record<TimeOfDay, { cx: number; cy: number; r: number }> = {
  morning: { cx: 750, cy: 228, r: 28 },
  afternoon: { cx: 880, cy: 158, r: 24 },
  evening: { cx: 860, cy: 218, r: 28 },
  night: { cx: 860, cy: 150, r: 18 },
};

const STARS: Array<[number, number]> = [
  [620, 140],
  [720, 166],
  [780, 130],
  [860, 210],
  [1010, 170],
  [950, 130],
  [1040, 150],
];

/**
 * The card's landscape: sky, sun (a crescent moon and stars at night) and two
 * hills. Decorative only. Every shape is colored by a class, so the palettes
 * live beside the card in GreetingCard.module.css.
 */
const GreetingScene = ({ timeOfDay }: { timeOfDay: TimeOfDay }): React.ReactElement => {
  const sun = SUN_POSITION[timeOfDay];
  const isNight = timeOfDay === "night";

  return (
    <svg
      className={styles.scene}
      viewBox="0 0 1200 300"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
      focusable="false"
    >
      <rect className={styles.sky} width="1200" height="300" />
      {isNight &&
        STARS.map(([x, y]) => (
          <circle key={`${x}-${y}`} className={styles.star} cx={x} cy={y} r={2} />
        ))}
      <circle className={styles.sun} cx={sun.cx} cy={sun.cy} r={sun.r} />
      {isNight && (
        // A sky-colored disc offset over the moon leaves a crescent.
        <circle
          className={styles.moonShadow}
          cx={sun.cx + sun.r * 0.55}
          cy={sun.cy - sun.r * 0.35}
          r={sun.r * 0.85}
        />
      )}
      <path
        className={styles.hillFar}
        d="M0 276 C 220 270, 380 250, 560 246 S 900 210, 1200 204 V 300 H 0 Z"
      />
      <path
        className={styles.hillNear}
        d="M0 290 C 260 286, 460 270, 700 268 S 1000 248, 1200 242 V 300 H 0 Z"
      />
    </svg>
  );
};

/**
 * A page's full-width greeting: a landscape for the viewer's time of day,
 * today's date, and the action most visits come for. The time of day is read
 * from the viewer's clock, and picks both the scene and the palette.
 *
 * Renders via Astryx's Card/Text/Button, so this MUST be rendered inside an
 * Astryx `<Theme>` scope.
 */
const GreetingCard = ({
  description,
  actionLabel,
  actionHref,
  actionIcon,
}: GreetingCardProps): React.ReactElement => {
  const now = new Date();
  const timeOfDay = timeOfDayAt(now.getHours());
  const today = now.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <Card
      variant="transparent"
      padding={6}
      className={[styles.card, PALETTE_CLASS[timeOfDay]].join(" ")}
    >
      <GreetingScene timeOfDay={timeOfDay} />
      <div className={styles.content}>
        <VStack gap={6} align="start">
          <VStack gap={2}>
            <Text type="supporting">{today}</Text>
            <Text as="p" type="display-2" weight="semibold">
              {GREETINGS[timeOfDay]}
            </Text>
            <Text type="body" color="secondary">
              {description}
            </Text>
          </VStack>
          <Button
            label={actionLabel}
            variant="primary"
            icon={actionIcon ? <Icon icon={actionIcon} /> : undefined}
            href={actionHref}
            className={styles.action}
          />
        </VStack>
      </div>
    </Card>
  );
};

export default GreetingCard;
